import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { unitLabel } from '../../../../shared/contracts/unit-label.js';
import { billRefLabel, dealerLabel, type BillRef } from '../../../../shared/contracts/bill-ref.js';
import { M13_CLAIM_STATUSES } from '../../../../shared/contracts/backend-enums.js';

/** The work one line of a bill bills (m13 common/bill-ref BilledWork). */
interface BilledWork {
  readonly jobRef: string | null;
  readonly brandingTypeName: string | null;
  readonly brandingItemName: string | null;
  readonly quantity: number;
  readonly uomCode: string | null;
  readonly dealerName: string | null;
  readonly dealerCode: string | null;
  readonly site: string | null;
}

/** One bill of GET /v1/billing/claims, with the work it bills. */
interface BillRow {
  readonly claimId: string;
  /** The job, dealer and date the bill is known by. */
  readonly bill: BillRef;
  readonly invoiceNumber: string | null;
  readonly invoiceDate: string | null;
  readonly vendorCode: string | null;
  readonly vendorName: string | null;
  readonly status: string;
  readonly totalClaimAmount: number;
  readonly works: readonly BilledWork[];
}

/** The statuses a bill has (M13ClaimStatus), in the order it moves through them. */
const STATUSES = ['ALL', ...M13_CLAIM_STATUSES];

const MUTED: React.CSSProperties = { color: 'var(--color-text-muted, #656d76)' };

/**
 * Historical Bills (owner, 28 Oct): every bill prepared from Completed Jobs,
 * each row carrying the job it bills so a bill is found by its job. The link
 * opens the bill, which is printed or saved as PDF from there. View only:
 * bills are raised and forwarded on Completed Jobs (owner, 29 Oct). A vendor's
 * login sees its own bills only (the server decides, not this page).
 */
export function HistoricalBillsPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Amounts and quantities follow the tenant's DISPLAY_NUMBER_FORMAT.
  const { money, decimal } = useBusinessNumber();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { day } = useBusinessDay();
  const [bills, setBills] = useState<readonly BillRow[]>([]);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const qs = new URLSearchParams({ limit: '200' });
    if (statusFilter !== 'ALL') qs.set('status', statusFilter);
    apiRequest<{ items: BillRow[]; total: number }>(`/v1/billing/claims?${qs.toString()}`, { token })
      .then((data) => { if (alive) setBills(data.items ?? []); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token, statusFilter]);

  // A bill normally bills one job; a line per piece of work when it bills more.
  const perWork = (b: BillRow, cell: (w: BilledWork) => ReactNode): ReactNode =>
    b.works.length === 0 ? '\u2014' : b.works.map((w, i) => <div key={i}>{cell(w)}</div>);

  return (
    <main aria-label="historical-bills">
      <h1 style={{ marginBottom: 16 }}>{t('billing.history.title')}</h1>
      <p style={{ ...MUTED, marginBottom: 12 }}>{t('billing.history.intro')}</p>

      {error && <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{error}</p>}

      <section style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <label htmlFor="bill-status" style={{ fontWeight: 600 }}>{t('billing.status.statusLabel')}</label>
        <select id="bill-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="select-status-filter"
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--color-border-default, #d0d7de)', fontSize: 14 }}>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </section>

      {bills.length === 0 && !error ? (
        <p data-testid="no-bills" style={MUTED}>{t('billing.history.none')}</p>
      ) : (
        <DataTable<BillRow>
          testId="historical-bills-table"
          rowKey={(b) => b.claimId}
          rowTestId={(b) => `bill-row-${b.claimId}`}
          columns={[
            {
              key: 'bill', header: t('billing.history.col.bill'), render: (b) => (
                <Link to={`/billing/claims/${b.claimId}/bill`} data-testid={`open-bill-${b.claimId}`}>
                  {b.invoiceNumber ?? billRefLabel(b.bill, day)}
                </Link>
              ),
            },
            { key: 'job', header: t('billing.completed.colJob'), render: (b) => perWork(b, (w) => w.jobRef ?? '\u2014') },
            {
              key: 'branding', header: t('billing.completed.colBranding'),
              render: (b) => perWork(b, (w) => [w.brandingTypeName, w.brandingItemName].filter(Boolean).join(' / ') || '\u2014'),
            },
            {
              key: 'qty', header: t('billing.completed.colQty'),
              render: (b) => perWork(b, (w) => `${decimal(w.quantity)} ${unitLabel(w.uomCode ?? '')}`.trim()),
            },
            {
              key: 'site', header: t('billing.history.col.site'),
              render: (b) => perWork(b, (w) => [w.dealerName && dealerLabel(w.dealerName, w.dealerCode), w.site].filter(Boolean).join(' \u00b7 ') || '\u2014'),
            },
            { key: 'vendor', header: t('billing.status.col.vendor'), render: (b) => b.vendorName ?? b.vendorCode ?? '\u2014' },
            { key: 'date', header: t('billing.history.col.invoiceDate'), render: (b) => (b.invoiceDate ? day(b.invoiceDate) : '\u2014') },
            { key: 'amount', header: t('billing.status.col.amount'), render: (b) => money(b.totalClaimAmount) },
            { key: 'status', header: t('billing.status.col.status'), render: (b) => <span data-testid={`status-${b.claimId}`}>{b.status}</span> },
          ]}
          rows={bills}
        />
      )}
    </main>
  );
}